import { useCallback, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Checkbox, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { User } from 'lucide-react'

import { routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import { NotificationType } from 'src/graphql/types/notifications'
import { TaskStatus } from 'src/graphql/types/tasks'
import IconCheckmarkCircle from 'src/icons/IconCheckmarkCircle'
import { buildFullName } from 'src/lib/formatters'
import { replaceRecruitersIdWithName } from 'src/lib/recruiters.utils'
import SMSInput from 'src/pages/ApplicantDetailsPage/tabs/ActivityLogTab/components/ActivityLogInput/SMSInput/SMSInput'

import AttachmentsRow from './Components/AttachmentsRow'
import NotificationActions from './Components/NotificationActions'
import { getNotificationConfig } from './notificationConfig'
import {
  getSwitchCompanyTooltip,
  getVoENotificationRoute,
  handleNotificationNavigation,
  isNotificationFromDifferentCompany as checkIsFromDifferentCompany,
} from './utils/notificationUtils'

interface NotificationCardProps {
  notification: any
  updateCompanyAndRedirect: (employeeId: string, route: string) => void
  markAsRead: (options: { variables: { id: string } }) => void
  onReplySMS: (params: { notificationId: string; message: string }) => void
  isSelected?: boolean
  onToggleSelect?: () => void
}

const NotificationCard = ({
  notification,
  updateCompanyAndRedirect,
  markAsRead,
  onReplySMS,
  isSelected = false,
  onToggleSelect,
}: NotificationCardProps) => {
  const [isReplying, setIsReplying] = useState(false)
  const [hover, setHover] = useState(false)
  const { currentUser } = useAuth()
  const applicantExists = notification.applicant
  const voeRoute = getVoENotificationRoute(notification)
  const canOpenVoe =
    notification.type === NotificationType.VOE_RESPONSE && Boolean(voeRoute)
  const isTaskCompleted =
    notification.type === NotificationType.TASK &&
    notification.task?.status === TaskStatus.COMPLETED

  // For tasks, always use notification.title; for emails, use applicant name or notification.title; otherwise use applicant name or notification.title
  const title =
    notification.type === NotificationType.TASK
      ? notification.title
      : notification.type === NotificationType.EMAIL
      ? applicantExists
        ? buildFullName(notification.applicant)
        : notification.title
      : applicantExists
      ? buildFullName(notification.applicant)
      : notification.title

  const isNotificationFromDifferentCompany = checkIsFromDifferentCompany(
    notification,
    currentUser.companyId
  )

  const { data: employees, loading: employeesLoading } = useQuery(
    GET_EMPLOYEES_ONLY,
    {
      variables: {
        pageInfo: { offset: 0, limit: 100 },
        filters: {
          roleTypes: 'RECRUITER',
          ...(isNotificationFromDifferentCompany && {
            companyId: notification.company?.companyId,
          }),
        },
      },
    }
  )
  const recruiters = employeesLoading
    ? []
    : employees?.employees?.items?.map((employee) => ({
        label: buildFullName(employee),
        value: employee.employeeId,
      })) ?? []

  const navigateFromNotification = useCallback(
    (route: string) => {
      handleNotificationNavigation(
        route,
        isNotificationFromDifferentCompany,
        notification.employeeId,
        updateCompanyAndRedirect
      )
    },
    [
      isNotificationFromDifferentCompany,
      notification.employeeId,
      updateCompanyAndRedirect,
    ]
  )

  const handleApplicantNavigate = useCallback(() => {
    if (notification.type === NotificationType.VOE_RESPONSE) {
      const voeRoute = getVoENotificationRoute(notification)
      if (voeRoute) {
        navigateFromNotification(voeRoute)
      }
      return
    }

    if (applicantExists) {
      const route = routes.applicantDetails({
        id: notification.applicant.applicantId,
        tab: 'application',
      })

      navigateFromNotification(route)
    }
  }, [applicantExists, notification, navigateFromNotification])

  const onHover = useCallback(() => {
    setHover(true)
  }, [])

  const onLeave = useCallback(() => {
    setHover(false)
  }, [])

  return (
    <div
      className={`group relative flex flex-row gap-3 border-b-[1px] border-doubleNickel-gray-200 px-0 py-2 hover:rounded-lg hover:bg-doubleNickel-gray-50 ${
        isSelected ? 'bg-doubleNickel-brand-25' : ''
      }`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <div className="flex items-start pt-0.5">
        <Checkbox
          size={14}
          checked={isSelected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          classNames={{
            input:
              'checked:bg-doubleNickel-brand-500 checked:border-doubleNickel-brand-500',
          }}
        />
      </div>
      <div className="flex items-start">
        {(() => {
          const { icon, label } = getNotificationConfig(notification.type)
          return (
            <Tooltip label={label}>
              <div className="flex-shrink-0">{icon}</div>
            </Tooltip>
          )
        })()}
      </div>
      {hover && (
        <NotificationActions
          isRead={notification.read}
          notificationType={notification.type}
          onMarkAsRead={() =>
            markAsRead({ variables: { id: notification.notificationId } })
          }
          onReply={
            notification.type === NotificationType.SMS
              ? () => {
                  if (isNotificationFromDifferentCompany) {
                    const route = routes.applicantDetails({
                      id: notification.applicant?.applicantId,
                      tab: 'activitylog',
                      activityType: 'SMS',
                    })
                    navigateFromNotification(route)
                  } else {
                    setIsReplying(!isReplying)
                  }
                }
              : undefined
          }
          onView={() => {
            let route: string

            switch (notification.type) {
              case NotificationType.SMS:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'activitylog',
                  activityType: 'SMS',
                })
                break
              case NotificationType.EMAIL:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'activitylog',
                  activityType: 'EMAIL',
                })
                break
              case NotificationType.TASK:
                if (notification.applicant?.applicantId) {
                  // Deep-link to the applicant with the task modal open
                  const base = routes.applicantDetails({
                    id: notification.applicant.applicantId,
                    tab: 'application',
                  })
                  route = `${base}?taskId=${notification.taskId}`
                } else {
                  // No applicant on the task — fall back to the Tasks page
                  route = routes.tasksWithId({
                    id: notification.taskId,
                  })
                }
                break
              case NotificationType.NOTE:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'activitylog',
                  activityType: 'NOTE',
                })
                break
              case NotificationType.ORDER_UPDATE:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'orders',
                })
                break
              case NotificationType.VOE_RESPONSE:
                route =
                  getVoENotificationRoute(notification) ??
                  routes.applicantDetails({
                    id: notification.applicant?.applicantId,
                    tab: 'voe',
                  })
                break
              case NotificationType.TRAINING:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'training',
                })
                break
              default:
                route = routes.applicantDetails({
                  id: notification.applicant?.applicantId,
                  tab: 'application',
                })
            }

            navigateFromNotification(route)
          }}
        />
      )}
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-row items-center gap-4">
          <div className="flex flex-1 flex-row items-center gap-1">
            {notification.type === NotificationType.EMAIL ? (
              <>
                <Tooltip label={title}>
                  <Box
                    className="truncate text-xs font-medium text-black hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (applicantExists) {
                        handleApplicantNavigate()
                      }
                    }}
                  >
                    {title}
                  </Box>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip label={title}>
                  <Box
                    className={`truncate text-xs font-medium text-black ${
                      (applicantExists || canOpenVoe) &&
                      notification.type !== NotificationType.TASK &&
                      'hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline'
                    } ${
                      isTaskCompleted
                        ? 'text-doubleNickel-gray-500 line-through'
                        : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (notification.type !== NotificationType.TASK) {
                        handleApplicantNavigate()
                      }
                    }}
                  >
                    {title}
                  </Box>
                </Tooltip>
                {isTaskCompleted && (
                  <Box className="flex flex-shrink-0 items-center gap-1 rounded bg-doubleNickel-success-50 px-1.5 py-0.5 text-[10px] font-medium text-doubleNickel-success-700">
                    <IconCheckmarkCircle className="h-2.5 w-2.5 fill-none stroke-doubleNickel-success-700" />
                    Completed
                  </Box>
                )}
              </>
            )}
          </div>

          <div className="ml-auto flex flex-row items-center gap-4">
            <div className="text-[10px] font-normal text-doubleNickel-gray-600">
              {dayjs(notification.createdAt).format('MM/DD/YYYY h:mm A')}
            </div>
          </div>
        </div>

        {notification.type === NotificationType.TASK && applicantExists && (
          <div className="flex flex-row items-center gap-1 text-xs text-doubleNickel-gray-600">
            <Tooltip label={`View ${buildFullName(notification.applicant)}`}>
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  handleApplicantNavigate()
                }}
                className="flex w-fit flex-row items-center gap-1 hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline"
              >
                <User className="h-3 w-3" />
                {buildFullName(notification.applicant)}
              </Box>
            </Tooltip>
          </div>
        )}
        {isNotificationFromDifferentCompany && (
          <div className="flex flex-row items-center gap-1 text-xs font-medium text-doubleNickel-gray-600">
            <Tooltip
              label={getSwitchCompanyTooltip(notification.company?.companyName)}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  navigateFromNotification(routes.dashboard())
                }}
                className="flex flex-row gap-1 hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline"
              >
                Switch to {notification.company?.companyName}
              </Box>
            </Tooltip>
          </div>
        )}
        <div
          className={`text-xs text-doubleNickel-gray-700 ${
            (notification.type === NotificationType.NOTE ||
              notification.type === NotificationType.SMS) &&
            'italic'
          } ${hover ? 'line-clamp-none' : 'line-clamp-2'}`}
        >
          {employeesLoading ? (
            ''
          ) : (
            <>
              <span className="font-semibold not-italic text-doubleNickel-gray-600">
                {getNotificationConfig(notification.type).label}:{' '}
              </span>
              {notification.type === NotificationType.NOTE ||
              notification.type === NotificationType.SMS ? (
                <span>
                  &ldquo;
                  {replaceRecruitersIdWithName(
                    notification.message,
                    recruiters
                  )}
                  &rdquo;
                </span>
              ) : (
                replaceRecruitersIdWithName(notification.message, recruiters)
              )}
            </>
          )}
        </div>
        {notification.type === NotificationType.SMS &&
          notification.activityLog?.documents &&
          notification.activityLog?.documents.length > 0 && (
            <AttachmentsRow
              disabled={isNotificationFromDifferentCompany}
              disabledText={getSwitchCompanyTooltip(
                notification.company?.companyName
              )}
              attachments={notification.activityLog.documents}
              applicantId={notification.applicant?.applicantId}
            />
          )}
        <div className="flex flex-row justify-between">
          {/* Task button removed per user request */}
        </div>
        {isReplying && (
          <div className="mt-1 [&_button]:h-7 [&_button]:px-2 [&_button]:text-[11px] [&_div]:gap-0.5 [&_div]:px-1.5 [&_div]:py-0.5 [&_form]:items-end [&_form]:gap-1.5 [&_textarea]:max-h-[50px] [&_textarea]:min-h-0 [&_textarea]:py-0.5 [&_textarea]:text-xs [&_textarea]:leading-tight">
            <SMSInput
              applicantId={notification.applicant?.applicantId}
              sendSMS={({ content }) =>
                onReplySMS({
                  notificationId: notification.notificationId,
                  message: content,
                })
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationCard
